/* SF3 — timeline difference scroll animation.
 *
 * Architecture (rewritten for fluidity):
 *   • The continuous vertical progress bar is the ONLY scroll-driven value.
 *     A single CSS var (--sf3-progress) is updated each rAF frame and feeds
 *     a linear-gradient stop. No CSS transition on it — JS drives directly.
 *   • Per-milestone state uses DISCRETE classes (.is-active, .is-past) so
 *     CSS transitions only fire on the rare class change, not per frame.
 *     This eliminates the chase-loop / lag that the previous per-frame
 *     --m-active drove on opacity, scale, box-shadow & background.
 *   • rAF runs continuously while the section is anywhere near the viewport
 *     (IntersectionObserver gates it), so iOS momentum scroll keeps updating
 *     even when scroll events go silent.
 */
(function () {
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function initTimeline(section) {
    if (!section || section.dataset.sf3DifferenceReady === 'true') return;
    section.dataset.sf3DifferenceReady = 'true';

    var list = section.querySelector('.sf3-difference__timeline');
    var milestones = Array.prototype.slice.call(section.querySelectorAll('[data-sf3-milestone]'));
    if (!list || !milestones.length) return;

    var rafId = null;
    var isObserving = false;
    var lastActiveIndex = -1;
    var lastProgressStr = '';

    function update() {
      var viewportH = window.innerHeight;
      var anchor = viewportH * 0.5;
      var listRect = list.getBoundingClientRect();
      var listSpan = Math.max(listRect.height, 1);

      // 1) Continuous progress 0–1 (anchor sweeping over the list).
      var progress = clamp((anchor - listRect.top) / listSpan, 0, 1);
      var progressStr = (progress * 100).toFixed(2) + '%';
      if (progressStr !== lastProgressStr) {
        section.style.setProperty('--sf3-progress', progressStr);
        lastProgressStr = progressStr;
      }

      // 2) Discrete active milestone (closest to the anchor).
      var activeIndex = 0;
      var smallestDelta = Infinity;
      for (var i = 0; i < milestones.length; i++) {
        var rect = milestones[i].getBoundingClientRect();
        var centerY = rect.top + rect.height * 0.5;
        var delta = Math.abs(centerY - anchor);
        if (delta < smallestDelta) {
          smallestDelta = delta;
          activeIndex = i;
        }
      }

      if (activeIndex !== lastActiveIndex) {
        for (var j = 0; j < milestones.length; j++) {
          milestones[j].classList.toggle('is-active', j === activeIndex);
          milestones[j].classList.toggle('is-past', j < activeIndex);
        }
        lastActiveIndex = activeIndex;
      }
    }

    function tick() {
      update();
      if (isObserving) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    }

    function start() {
      if (rafId !== null) return;
      isObserving = true;
      rafId = window.requestAnimationFrame(tick);
    }

    function stop() {
      isObserving = false;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    // Seed initial state.
    update();

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) start();
          else stop();
        });
      }, { rootMargin: '200px 0px 200px 0px' });
      io.observe(section);
    } else {
      start();
    }
  }

  function init() {
    document.querySelectorAll('[data-sf3-difference]').forEach(initTimeline);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
